import React from 'react';
import Calendar from './Calendar';
import TimeSlotGrid from './TimeSlotGrid';
import ProfessionalList from './ProfessionalList';
import ProfessionalSelector from '../../ProfessionalSelector';
import CustomNotice from '../../shared/CustomNotice';

/**
 * Componente de apresentação para a seleção de data, horário e profissional
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Date} props.selectedDate - Data selecionada
 * @param {Function} props.onDateChange - Função chamada ao mudar a data
 * @param {Array} props.availableTimeSlots - Lista de horários disponíveis
 * @param {string} props.selectedTime - Horário selecionado
 * @param {Function} props.onTimeSelect - Função chamada ao selecionar um horário
 * @param {Array} props.availableProfessionals - Lista de profissionais disponíveis
 * @param {Object} props.selectedProfessional - Profissional selecionado
 * @param {Function} props.onProfessionalSelect - Função chamada ao selecionar um profissional
 * @param {Array} props.selectedServices - Serviços selecionados
 * @param {string} props.primaryColor - Cor primária para destaque
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Function} props.onBack - Função chamada ao voltar
 * @param {Function} props.onContinue - Função chamada ao continuar
 * @param {boolean} props.continueDisabled - Indica se o botão continuar deve estar desabilitado
 * @param {boolean} props.showAllSlots - Mostrar todos os slots, não apenas disponíveis
 * @param {boolean} props.allowManualTimeEntry - Indica se permite entrada manual de horário
 * @param {boolean} props.isAdmin - Indica se o usuário é administrador
 * @param {Object} props.salonConfig - Configurações do salão
 */
const DateTimeSelectionView = ({
  selectedDate,
  onDateChange,
  disabledDates = [],
  availableTimeSlots = [],
  selectedTime,
  onTimeSelect,
  availableProfessionals = [],
  selectedProfessional,
  onProfessionalSelect,
  selectedServices = [],
  primaryColor = '#4F46E5',
  isLoading = false,
  onBack,
  onContinue,
  continueDisabled = false,
  NavigationButtons,
  showAllSlots = false,
  allowManualTimeEntry = false,
  isAdmin = false,
  salonConfig = {}
}) => {
  const customNotices = salonConfig?.customNotices || {};
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-64 bg-gray-100 rounded-lg mb-4"></div>
          <div className="h-32 bg-gray-100 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      {/* Aviso personalizado geral */}
      {customNotices.generalNotice?.enabled && 
       (customNotices.generalNotice.displayLocation === 'all' || 
        customNotices.generalNotice.displayLocation === 'dateTimeSelection') && (
        <CustomNotice 
          message={customNotices.generalNotice.message} 
          variant={customNotices.generalNotice.variant || "gray"} 
          className="mb-4"
        />
      )}
      
      {/* Seleção de Data */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">Selecione a data</h3>
        <Calendar
          selectedDate={selectedDate}
          onChange={onDateChange}
          disabledDates={disabledDates}
          primaryColor={primaryColor}
        />
      </div>
      
      {/* Seleção de Horário */}
      {selectedDate && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Selecione o horário</h3>
          <TimeSlotGrid
            availableTimeSlots={availableTimeSlots}
            selectedTime={selectedTime}
            onTimeSelect={onTimeSelect}
            primaryColor={primaryColor}
            showAllSlots={showAllSlots}
            isAdmin={isAdmin}
          />
        </div>
      )}
      
      {/* Seleção de Profissional */}
      {selectedDate && selectedTime && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Selecione o profissional</h3>
          <ProfessionalSelector
            availableProfessionals={availableProfessionals}
            selectedProfessional={selectedProfessional}
            onProfessionalSelect={onProfessionalSelect}
            primaryColor={primaryColor}
            isAdmin={isAdmin}
          />
        </div>
      )}
      
      {/* Botões de Navegação */}
      <NavigationButtons
        onBack={onBack}
        onContinue={onContinue}
        continueDisabled={continueDisabled}
        showBack={true}
        primaryColor={primaryColor}
      />
    </div>
  );
};

export default DateTimeSelectionView; 